import React, { useContext } from 'react'
import styled from 'styled-components'
import { Heading } from 'sutra/components/Text/Heading'
import useResizeObserver from 'use-resize-observer'

import { pageBackgroundGrey } from 'assets/styles/variables'
import { Loader } from 'components'
import { ShareResource } from 'components/share-resource/ShareResource/ShareResource'
import { AlertFilterCards } from 'features/alerts/components/AlertFilterCards/AlertFilterCards'
import { AlertKeywordSection } from 'features/alerts/components/AlertKeywordSection/AlertKeywordSection'
import { toAlertShareMembers } from 'features/alerts/components/view-alert/alert-share-members'
import { conditionOptionsList } from 'features/alerts/constants'
import { Header } from 'features/alerts/features/create-alert/create-alert.styles'
import { AlertResponse } from 'features/alerts/service'
import { AuthContext } from 'features/auth/auth-context'
import type { FilterOption } from 'features/filters/types'

type Props = {
  alertData?: AlertResponse
  setIsOpen: (isOpen: boolean) => void
  refetch: () => Promise<unknown>
  filterOptions: FilterOption[]
}
const ViewAlert: React.FC<Props> = ({
  alertData,
  setIsOpen,
  filterOptions,
}) => {
  const { alert, owners, viewers } = alertData || {}

  const authContext = useContext(AuthContext)
  const currentUserEmail = authContext?.user?.attributes?.email
  const isCagUseStdDev = !!currentUserEmail?.includes('@conagrafoods.com')

  const { ref: headerRef, height: headerHeight = 1 } =
    useResizeObserver<HTMLDivElement>()
  const { ref: _footerRef, height: footerHeight = 1 } =
    useResizeObserver<HTMLDivElement>()

  const condition = conditionOptionsList.find(
    (el) => el.value === alert?.trigger_type,
  )

  return (
    <Container>
      <Header ref={headerRef}>
        <HeaderRow>
          <Heading as="h4" $level="5">
            {alert?.title}
          </Heading>
          <CloseTitle
            onClick={() => {
              // TODO reset form if edit?
              setIsOpen(false)
            }}
          >
            <Heading as="h4" $level="5">
              X
            </Heading>
          </CloseTitle>
        </HeaderRow>
      </Header>
      <Body $headerHeight={headerHeight} $footerHeight={footerHeight}>
        <div>
          <SummarySection>
            <Heading as="h5" $level="6">
              Send an Alert when {alert?.alert_type}{' '}
              {condition?.type === 'limit' ? (
                <>
                  {conditionOptionsList.find(
                    (el) => el.value === alert?.trigger_type,
                  )?.label ?? ''}{' '}
                  {alert?.compare_value}.
                </>
              ) : (
                <>
                  {conditionOptionsList.find(
                    (el) => el.value === alert?.trigger_type,
                  )?.label ?? 'has changed'}{' '}
                  {(isCagUseStdDev &&
                    `${
                      conditionOptionsList.find(
                        (el) => el.value === alert?.trigger_type,
                      )?.std_dev_label
                    } `) ??
                    ''}
                  in the last {alert?.time_interval?.toLowerCase()}.
                </>
              )}
            </Heading>
            <AlertFilterCards
              filters={alert?.filters_v2?.filters ?? []}
              filterOptions={filterOptions}
            />

            <AlertKeywordSection
              searchCriteria={alert?.filters_v2?.searchCriteria ?? {}}
            />
          </SummarySection>
          <Section>
            <ShareResource
              members={toAlertShareMembers(
                owners ?? [],
                viewers ?? [],
                currentUserEmail,
              )}
            />
          </Section>
        </div>
      </Body>

      {!alert && (
        <Overlay>
          <Loader />
          Loading...
        </Overlay>
      )}
    </Container>
  )
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  //gap: 8px;
`

const Body = styled.div<{ $headerHeight: number; $footerHeight: number }>`
  display: flex;
  flex-direction: column;
  overflow: auto;
  height: calc(
    100vh - ${(props) => props.$headerHeight}px -
      ${(props) => props.$footerHeight}px - 50px
  );
  padding-bottom: var(--default-padding);
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 10px;
`

const CloseTitle = styled.div`
  cursor: pointer;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--default-padding-half) var(--default-padding);
  border-bottom: 1px solid #f2f2f2;
`

const SummarySection = styled(Section)`
  background: ${pageBackgroundGrey};

  & > h5 {
    margin-bottom: 8px;
  }
`

const Overlay = styled.div`
  background: #fff;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

export default ViewAlert
